'use client'

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { Shield, RefreshCw } from 'lucide-react'

interface BillingToggleProps {
  isAnnual: boolean
  setIsAnnual: (value: boolean) => void
  className?: string
}

/**
 * Top-level billing cycle toggle (Monthly/Yearly)
 * Shows trust signals below the toggle
 */
export function BillingToggle({
  isAnnual,
  setIsAnnual,
  className,
}: BillingToggleProps) {
  const t = useTranslations('pricing-v3')

  return (
    <div className={cn('flex flex-col items-center gap-4', className)}>
      {/* Toggle buttons */}
      <div className="inline-flex items-center bg-white/10 rounded-full p-1 backdrop-blur-sm">
        <button
          type="button"
          onClick={() => setIsAnnual(false)}
          className={cn(
            'px-6 py-2 rounded-full text-sm font-medium transition-all',
            !isAnnual
              ? 'bg-white text-gray-900 shadow-md'
              : 'text-white hover:text-white/80',
          )}
        >
          {t('billing.monthly')}
        </button>
        <button
          type="button"
          onClick={() => setIsAnnual(true)}
          className={cn(
            'px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2',
            isAnnual
              ? 'bg-white text-gray-900 shadow-md'
              : 'text-white hover:text-white/80',
          )}
        >
          {t('billing.annual')}
          {isAnnual && (
            <Badge className="bg-green-500 text-white hover:bg-green-500 text-xs px-2 py-0">
              {t('billing.save_badge')}
            </Badge>
          )}
        </button>
      </div>

      {/* Trust signals */}
      <div className="flex items-center gap-6 text-xs text-white/70">
        <div className="flex items-center gap-1.5">
          <RefreshCw className="w-4 h-4" />
          <span>Cancel anytime</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Shield className="w-4 h-4" />
          <span>7-day money-back</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span>•</span>
          <span>Upgrade anytime</span>
        </div>
      </div>
    </div>
  )
}
